import React, { useMemo, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useParams } from 'react-router-dom';
import { Spinner, Button, Alert } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { NoData } from '../../../components/SharedComponents/NoData';
import PageTitle from '../../../layouts/PageTitle';

// Lucide React Icons
import {
  Plus,
  RefreshCw,
  Loader2,
  FileText,
  Building,
  Receipt,
  Clock,
  AlertTriangle,
  CheckCircle,
  Search,
  Paperclip,
  ShieldCheck,
  GripVertical,
  Undo2,
  TrendingUp
} from 'lucide-react';
import { useAccountingSettings } from '../../../../context/AccountingSettingsContext';
import VerificationModal from './VerificationModal';

// 3-State Kanban: Scanned → Approved → Paid
const LANE_CONFIG = [
  { key: 'Scanned', label: 'Skannade', color: '#6b7280', bg: '#f3f4f6' },
  { key: 'Approved', label: 'Godkända', color: '#1e40af', bg: '#eff6ff' },
  { key: 'Paid', label: 'Betalda & bokförda', color: '#166534', bg: '#f0fdf4' },
];

// Lane label lookup
const LANE_LABELS = {};
LANE_CONFIG.forEach(l => { LANE_LABELS[l.key] = l.label; });

// Drag transitions:
//   Scanned → Approved: cash method can approve directly; accrual redirects to detail view
//   Approved → Paid: redirects to detail view (needs payment + verification)
const getDragTransitions = (accountingMethod) => {
  return {
    'Scanned': ['Approved'],
    'Approved': ['Paid'],
    'Paid': []
  };
};

// Transitions that require the detail view (verification/payment data needed)
// For cash method, Scanned → Approved can be done directly
// Only payment requires detail view — approval is handled in kanban (both methods)
const getDetailRequiredTargets = (accountingMethod) => {
  return new Set(['Paid']);
};

// Format currency helper
const formatCurrency = (amount) =>
  new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);

// Supplier Invoice Card Component
const SupplierInvoiceCard = ({ invoice, onClick, isDragging, onUndo, showUndo }) => {
  const amount = parseFloat(invoice.amount) || 0;
  const isOverdue = invoice.is_overdue;
  const daysUntilDue = invoice.days_until_due;
  const hasAttachments = invoice.attachment_count > 0;
  const hasVerification = invoice.has_verification;

  const totalPaid = parseFloat(invoice.total_paid) || 0;
  const showPartialPayment = invoice.status === 'Approved' && totalPaid > 0;
  const paymentProgress = amount > 0 ? Math.min((totalPaid / amount) * 100, 100) : 0;

  const handleUndoClick = (e) => {
    e.stopPropagation();
    if (onUndo) onUndo(invoice);
  };

  return (
    <div
      className={`invoice-card${isDragging ? ' is-dragging' : ''}${isOverdue ? ' invoice-card-overdue' : ''}`}
      onClick={onClick}
      style={undefined}
    >
      {/* Top row: number + amount + undo */}
      <div className="d-flex align-items-start gap-2 mb-2">
        <div className="card-icon" style={{ color: '#f59e0b', background: 'rgba(245, 158, 11, 0.1)' }}>
          <Receipt size={12} />
        </div>
        <span
          className="card-invoice-number"
          title={invoice.invoice_number || ''}
        >
          {invoice.invoice_number || '-'}
        </span>
        <span style={{ fontWeight: '700', fontSize: '0.8125rem', color: 'var(--title)', whiteSpace: 'nowrap', marginLeft: 'auto' }}>
          {formatCurrency(amount)}
        </span>
        {showUndo && (
          <button
            className="undo-btn"
            onClick={handleUndoClick}
            title="Ångra"
          >
            <Undo2 size={12} />
          </button>
        )}
        <GripVertical size={14} className="drag-handle" />
      </div>

      {/* Supplier */}
      <div className="d-flex align-items-center gap-1 mb-2" style={{ fontSize: '0.75rem', color: 'var(--secondary)' }}>
        <Building size={11} style={{ opacity: 0.5 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {invoice.supplier_name || 'Okänd leverantör'}
        </span>
      </div>

      {/* Due date */}
      {invoice.due_date && (
        <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.6875rem', color: 'var(--secondary)' }}>
          <Clock size={10} style={{ opacity: 0.4 }} />
          <span style={{
            color: isOverdue ? '#dc2626' : (daysUntilDue <= 7 && daysUntilDue > 0) ? '#d97706' : undefined,
            fontWeight: isOverdue || (daysUntilDue <= 7 && daysUntilDue > 0) ? '600' : undefined
          }}>
            Förfaller {invoice.due_date}
            {isOverdue && ` (${Math.abs(daysUntilDue)}d sen)`}
            {!isOverdue && daysUntilDue <= 7 && daysUntilDue > 0 && ` (${daysUntilDue}d kvar)`}
          </span>
        </div>
      )}

      {/* Partial payment progress bar */}
      {showPartialPayment && (
        <div className="mt-2">
          <div className="d-flex align-items-center justify-content-between mb-1" style={{ fontSize: '0.625rem', color: '#1e40af' }}>
            <span>{formatCurrency(totalPaid)} av {formatCurrency(amount)} betalt</span>
          </div>
          <div style={{ height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${paymentProgress}%`,
              backgroundColor: '#3b82f6',
              borderRadius: '2px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>
      )}

      {/* Badges row */}
      {(hasAttachments || hasVerification || invoice.use_accrual_method === 1) && (
        <div className="d-flex align-items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px solid #f1f5f9' }}>
          {hasAttachments && (
            <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.625rem', color: '#0369a1' }}>
              <Paperclip size={9} />
              <span>{invoice.attachment_count}</span>
            </div>
          )}
          {hasVerification && (
            <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.625rem', color: '#166534' }}>
              <ShieldCheck size={9} />
              <span>Verifierad</span>
            </div>
          )}
          {invoice.use_accrual_method === 1 && (
            <div style={{ fontSize: '0.625rem', color: '#1e40af' }}>
              Lev.skuld
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SupplierInvoiceList = () => {
  const history = useHistory();
  const { companyId } = useParams();
  const { accountingSettings } = useAccountingSettings();
  const accountingMethod = accountingSettings?.accountingMethod || 'accrual';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [metrics, setMetrics] = useState(null);
  const [dragSourceStatus, setDragSourceStatus] = useState(null);
  const [paidDays, setPaidDays] = useState(30);
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [approvalInvoice, setApprovalInvoice] = useState(null);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [kanbanPaymentDate, setKanbanPaymentDate] = useState(null);
  const [kanbanPaymentAmount, setKanbanPaymentAmount] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const transitions = useMemo(() => getDragTransitions(accountingMethod), [accountingMethod]);
  const detailRequired = useMemo(() => getDetailRequiredTargets(accountingMethod), [accountingMethod]);

  // Fetch invoices + metrics together
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoicesResponse, metricsResponse] = await Promise.allSettled([
        axios.get(getApiUrl(`${companyId}/purchasing/supplier-invoices`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/purchasing/supplier-invoices/metrics`), { withCredentials: true })
      ]);

      if (invoicesResponse.status === 'fulfilled') {
        const data = invoicesResponse.value.data;
        setInvoices(Array.isArray(data) ? data : data?.data || []);
      } else {
        setError('Det gick inte att hämta leverantörsfakturorna. Försök igen senare.');
      }

      if (metricsResponse.status === 'fulfilled') {
        setMetrics(metricsResponse.value.data.data);
      }
    } catch (err) {
      setError('Det gick inte att hämta leverantörsfakturorna. Försök igen senare.');
      console.error('Error fetching supplier invoice data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [companyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Refetch when navigating back to this view (e.g., from detail view)
  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname.endsWith('/supplier-invoices') || location.pathname.match(/\/supplier-invoices\/?$/)) {
        fetchData();
      }
    });
    return unlisten;
  }, [history, fetchData]);

  // Fetch bank accounts for accrual payment dialog
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const response = await axios.get(
          getApiUrl(`${companyId}/accounting/accounts?scope=company`),
          { withCredentials: true }
        );
        const accounts = response.data.accounts || response.data || [];
        setBankAccounts(accounts.filter(a => {
          if (a.is_payment_account) return true;
          const num = parseInt(a.account_number);
          return num >= 1900 && num < 2000;
        }));
      } catch (err) {
        console.error('Error fetching bank accounts:', err);
      }
    };
    fetchBankAccounts();
  }, [companyId]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Refresh only metrics (after actions — no need to refetch all invoices)
  const refreshMetrics = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/metrics`),
        { withCredentials: true }
      );
      setMetrics(response.data.data);
    } catch (err) {
      console.error('Error refreshing metrics:', err);
    }
  };

  // Compute metrics from invoice data (fallback when /metrics endpoint returns partial data)
  const computedMetrics = useMemo(() => {
    const scannedInvoices = invoices.filter(inv => inv.status === 'Scanned');
    const overdueInvoices = invoices.filter(inv => inv.is_overdue && inv.status !== 'Paid');
    const paidInvoices = invoices.filter(inv => inv.status === 'Paid');
    const total = invoices.length;
    const processed = paidInvoices.length;

    return {
      scanned_count: scannedInvoices.length,
      scanned_amount: scannedInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
      overdue_count: overdueInvoices.length,
      overdue_amount: overdueInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
      paid_count: paidInvoices.length,
      paid_amount: paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
      efficiency: total > 0 ? Math.round((processed / total) * 100) : 0,
      processed_count: processed,
      total_invoices: total
    };
  }, [invoices]);

  // Handle undo action
  const handleUndo = useCallback(async (invoice) => {
    const isApproved = invoice.status === 'Approved';
    const isPaid = invoice.status === 'Paid';
    const isAccrual = invoice.use_accrual_method === 1;

    let confirmText = '';
    if (isApproved) {
      confirmText = isAccrual
        ? 'Godkännandet ångras och verifikationen annulleras.'
        : 'Godkännandet ångras.';
    } else if (isPaid) {
      confirmText = 'Betalningen reverseras och verifikationen annulleras.';
    }

    const result = await Swal.fire({
      title: 'Ångra status',
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Ja, ångra',
      cancelButtonText: 'Avbryt'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoice.invoice_id}/undo`),
        {},
        { withCredentials: true }
      );
      toast.success(`Faktura ${invoice.invoice_number || ''} har ångrad`);
      await fetchData();
    } catch (err) {
      const message = err.response?.data?.error || 'Kunde inte ångra statusändring';
      toast.error(message);
    }
  }, [companyId, fetchData]);

  // Handle cash method direct approval from kanban drag
  const handleCashApproval = useCallback(async (invoice) => {
    const result = await Swal.fire({
      title: 'Godkänn faktura',
      text: `Godkänn faktura ${invoice.invoice_number || ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1e40af',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Godkänn',
      cancelButtonText: 'Avbryt'
    });

    if (!result.isConfirmed) return;

    try {
      await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoice.invoice_id}/approve`),
        { transactions: [] },
        { withCredentials: true }
      );
      toast.success(`Faktura ${invoice.invoice_number || ''} godkänd`);
      await fetchData();
    } catch (err) {
      const message = err.response?.data?.error || 'Kunde inte godkänna fakturan';
      toast.error(message);
    }
  }, [companyId, fetchData]);

  // Handle accrual payment from kanban drag (single dialog: date + amount + bank account)
  const handleAccrualPayment = useCallback(async (invoice) => {
    const remaining = parseFloat(invoice.remaining_balance ?? invoice.amount);
    const fmtAmount = (v) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(v);

    // Fetch bank accounts on-demand to ensure they're available
    let accounts = bankAccounts;
    if (!accounts || accounts.length === 0) {
      try {
        const response = await axios.get(
          getApiUrl(`${companyId}/accounting/accounts?scope=company`),
          { withCredentials: true }
        );
        const all = response.data.accounts || response.data || [];
        accounts = all.filter(a => {
          if (a.is_payment_account) return true;
          const num = parseInt(a.account_number);
          return num >= 1900 && num < 2000;
        });
        setBankAccounts(accounts);
      } catch (err) {
        toast.error('Kunde inte hämta betalkonton');
        return;
      }
    }

    const accountOptions = accounts.map(a =>
      `<option value="${a.account_id}" data-number="${a.account_number}">${a.account_number} — ${a.account_name}</option>`
    ).join('');

    const result = await Swal.fire({
      title: 'Registrera betalning',
      html: `
        <div style="text-align:left; margin-top:0.5rem;">
          <p style="margin-bottom:1rem; color:#374151; font-size:0.875rem;">
            <strong>${invoice.invoice_number}</strong> — ${invoice.supplier_name || 'Okänd leverantör'}
          </p>
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalningsdatum</label>
          <input id="swal-pay-date" type="date" class="swal2-input" style="margin:0 0 1rem 0; width:100%;"
            value="${dayjs().format('YYYY-MM-DD')}" min="${invoice.invoice_date}" max="${dayjs().format('YYYY-MM-DD')}" />
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Belopp</label>
          <input id="swal-pay-amount" type="number" class="swal2-input" style="margin:0 0 0.25rem 0; width:100%;"
            value="${remaining.toFixed(2)}" min="0.01" step="0.01" />
          <div style="font-size:0.75rem; color:#6b7280; margin-bottom:1rem;">Kvarvarande: ${fmtAmount(remaining)}</div>
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalkonto</label>
          <select id="swal-pay-bank" class="swal2-select" style="margin:0; width:100%;">
            ${accountOptions || '<option value="">Inga betalkonton hittades</option>'}
          </select>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Avbryt',
      confirmButtonText: 'Bekräfta betalning',
      confirmButtonColor: '#166534',
      focusConfirm: false,
      preConfirm: () => {
        const date = document.getElementById('swal-pay-date').value;
        const amount = parseFloat(document.getElementById('swal-pay-amount').value);
        const bankSelect = document.getElementById('swal-pay-bank');
        const bankAccountId = parseInt(bankSelect.value);
        const bankAccountNumber = bankSelect.selectedOptions[0]?.dataset?.number;
        if (!date) { Swal.showValidationMessage('Betalningsdatum krävs'); return false; }
        if (!amount || amount <= 0) { Swal.showValidationMessage('Beloppet måste vara större än 0'); return false; }
        if (amount > remaining + 0.01) { Swal.showValidationMessage(`Beloppet överstiger kvarvarande (${fmtAmount(remaining)})`); return false; }
        if (!bankAccountId) { Swal.showValidationMessage('Välj ett betalkonto'); return false; }
        return { date, amount, bankAccountId, bankAccountNumber };
      }
    });

    if (!result.isConfirmed) return;
    const { date, amount, bankAccountId, bankAccountNumber } = result.value;

    try {
      await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/${invoice.invoice_id}/pay`),
        {
          payment_date: date,
          amount,
          transactions: [
            { account_id: 2440, amount, transaction_type: 'debit', description: 'Leverantörsskuld reglerad' },
            { account_id: bankAccountId, amount, transaction_type: 'credit', description: `Betalning via ${bankAccountNumber}` }
          ]
        },
        { withCredentials: true }
      );
      toast.success(`Faktura ${invoice.invoice_number || ''} betald och bokförd`);
      await fetchData();
    } catch (err) {
      const message = err.response?.data?.error || 'Kunde inte registrera betalningen';
      toast.error(message);
    }
  }, [companyId, fetchData, bankAccounts]);

  // Handle cash-method payment from kanban: date+amount dialog, then VerificationModal for account coding
  const handleCashPayment = useCallback(async (invoice) => {
    const remaining = parseFloat(invoice.remaining_balance ?? invoice.amount);
    const fmtAmount = (v) => new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(v);

    const result = await Swal.fire({
      title: 'Registrera betalning',
      html: `
        <div style="text-align:left; margin-top:0.5rem;">
          <p style="margin-bottom:1rem; color:#374151; font-size:0.875rem;">
            <strong>${invoice.invoice_number}</strong> — ${invoice.supplier_name || 'Okänd leverantör'}
          </p>
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Betalningsdatum</label>
          <input id="swal-pay-date" type="date" class="swal2-input" style="margin:0 0 1rem 0; width:100%;"
            value="${dayjs().format('YYYY-MM-DD')}" max="${dayjs().format('YYYY-MM-DD')}" />
          <label style="display:block; margin-bottom:0.25rem; font-size:0.875rem; font-weight:500;">Belopp</label>
          <input id="swal-pay-amount" type="number" class="swal2-input" style="margin:0 0 0.25rem 0; width:100%;"
            value="${remaining.toFixed(2)}" min="0.01" step="0.01" />
          <div style="font-size:0.75rem; color:#6b7280; margin-bottom:1rem;">Kvarvarande: ${fmtAmount(remaining)}</div>
        </div>
      `,
      showCancelButton: true,
      cancelButtonText: 'Avbryt',
      confirmButtonText: 'Fortsätt till kontering',
      confirmButtonColor: 'var(--primary)',
      focusConfirm: false,
      preConfirm: () => {
        const date = document.getElementById('swal-pay-date').value;
        const amount = parseFloat(document.getElementById('swal-pay-amount').value);
        if (!date) { Swal.showValidationMessage('Betalningsdatum krävs'); return false; }
        if (!amount || amount <= 0) { Swal.showValidationMessage('Beloppet måste vara större än 0'); return false; }
        if (amount > remaining + 0.01) { Swal.showValidationMessage(`Beloppet överstiger kvarvarande (${fmtAmount(remaining)})`); return false; }
        return { date, amount };
      }
    });

    if (!result.isConfirmed) return;
    setPaymentInvoice(invoice);
    setKanbanPaymentDate(result.value.date);
    setKanbanPaymentAmount(result.value.amount);
    setShowVerificationModal(true);
  }, []);

  // Handle cash payment confirmation from VerificationModal
  const handleCashPaymentConfirm = useCallback(async (verificationData) => {
    if (!paymentInvoice) return;
    try {
      await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/${paymentInvoice.invoice_id}/pay`),
        {
          payment_date: kanbanPaymentDate,
          amount: kanbanPaymentAmount,
          transactions: verificationData.transactions
        },
        { withCredentials: true }
      );
      setShowVerificationModal(false);
      setPaymentInvoice(null);
      setKanbanPaymentDate(null);
      setKanbanPaymentAmount(null);
      toast.success(`Faktura ${paymentInvoice.invoice_number} betald och bokförd`);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Kunde inte registrera betalningen';
      toast.error(msg);
    }
  }, [paymentInvoice, kanbanPaymentDate, kanbanPaymentAmount, companyId, fetchData]);

  // Group invoices by status
  const { groups, statusCounts, overdueCount } = useMemo(() => {
    let filtered = invoices;

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(inv =>
        (inv.invoice_number || '').toLowerCase().includes(term) ||
        (inv.supplier_name || '').toLowerCase().includes(term)
      );
    }

    const groups = {};
    LANE_CONFIG.forEach(lane => { groups[lane.key] = []; });

    filtered.forEach(inv => {
      if (groups[inv.status]) {
        groups[inv.status].push(inv);
      }
    });

    // Apply overdue filter for Scanned and Approved lanes
    if (showOverdueOnly) {
      ['Scanned', 'Approved'].forEach(key => {
        groups[key] = groups[key].filter(inv => inv.is_overdue);
      });
    }

    // Sort each group
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => new Date(b.invoice_date || b.created_at) - new Date(a.invoice_date || a.created_at));
    });

    // Filter Paid lane by recency
    if (groups['Paid'] && paidDays > 0) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - paidDays);
      groups['Paid'] = groups['Paid'].filter(inv => {
        const date = new Date(inv.payment_date || inv.updated_at || inv.created_at);
        return date >= cutoff;
      });
    }

    const statusCounts = {};
    LANE_CONFIG.forEach(lane => {
      statusCounts[lane.key] = invoices.filter(inv => inv.status === lane.key).length;
    });

    const overdueCount = invoices.filter(inv => inv.is_overdue && inv.status !== 'Paid').length;

    return { groups, statusCounts, overdueCount };
  }, [invoices, searchTerm, statusFilter, paidDays, showOverdueOnly]);

  // Drag handlers
  const handleDragStart = useCallback((start) => {
    setDragSourceStatus(start.source.droppableId);
  }, []);

  const handleDragEnd = useCallback(async (result) => {
    setDragSourceStatus(null);
    const { source, destination, draggableId } = result;

    if (!destination || source.droppableId === destination.droppableId) return;

    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    const allowed = transitions[fromStatus] || [];

    if (!allowed.includes(toStatus)) {
      toast.error(`Kan inte flytta från "${LANE_LABELS[fromStatus]}" till "${LANE_LABELS[toStatus]}"`);
      return;
    }

    const invoice = invoices.find(inv => inv.invoice_id === draggableId);
    if (!invoice) return;

    // Scanned → Approved
    // Use the invoice's own accounting method (based on its fiscal year), not the company-level setting
    if (fromStatus === 'Scanned' && toStatus === 'Approved') {
      const invoiceMethod = invoice.use_accrual_method ? 'accrual'
        : (invoice.accounting_method || accountingMethod);
      if (invoiceMethod === 'cash') {
        // Cash: simple approval
        handleCashApproval(invoice);
      } else {
        // Accrual: open VerificationModal for account coding
        setApprovalInvoice(invoice);
        setShowVerificationModal(true);
      }
      return;
    }

    // Approved → Paid
    if (fromStatus === 'Approved' && toStatus === 'Paid') {
      const effectiveMethod = invoice.use_accrual_method ? 'accrual'
        : (invoice.accounting_method || accountingMethod);
      if (effectiveMethod === 'accrual') {
        // Accrual: simple dialog (date+amount+bank) — transactions are deterministic (2440 → bank)
        handleAccrualPayment(invoice);
      } else {
        // Cash: need full account coding — show payment date/amount dialog, then VerificationModal
        handleCashPayment(invoice);
      }
      return;
    }

    // Fallback: redirect to detail view
    history.push(`/${companyId}/supplier-invoices/${draggableId}`);
  }, [transitions, invoices, companyId, history, handleCashApproval, accountingMethod]);

  // Handle accrual approval from VerificationModal
  const handleAccrualApprovalConfirm = useCallback(async (verificationData) => {
    if (!approvalInvoice) return;
    try {
      await axios.post(
        getApiUrl(`${companyId}/purchasing/supplier-invoices/${approvalInvoice.invoice_id}/approve`),
        {
          transactions: verificationData.transactions,
          date: approvalInvoice.invoice_date,
          description: `Leverantörsfaktura ${approvalInvoice.invoice_number} — ${approvalInvoice.supplier_name}`
        },
        { withCredentials: true }
      );
      setShowVerificationModal(false);
      setApprovalInvoice(null);
      toast.success(`Faktura ${approvalInvoice.invoice_number} godkänd och bokförd`);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Kunde inte godkänna fakturan';
      toast.error(msg);
    }
  }, [approvalInvoice, companyId]);

  const isValidDropTarget = useCallback((laneKey) => {
    if (!dragSourceStatus) return true;
    const allowed = transitions[dragSourceStatus] || [];
    return allowed.includes(laneKey);
  }, [dragSourceStatus, transitions]);

  if (loading && !refreshing) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin { animation: spin 1s linear infinite; }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.25rem;
        }
        .stat-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          overflow: hidden;
        }
        .stat-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
        }
        .stat-icon {
          width: 48px; height: 48px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 1rem;
          position: relative;
        }
        .stat-value { font-size: 1.5rem; font-weight: 700; margin-bottom: 0.375rem; }
        .stat-label { color: #6b7280; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
        .stat-subtitle { color: #9ca3af; font-size: 0.6875rem; }

        /* Kanban Board */
        .invoice-board {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 1rem;
        }
        .invoice-lane {
          flex: 1;
          min-width: 0;
          background: #f8f9fb;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          max-height: calc(100vh - 340px);
          min-height: 400px;
          transition: all 0.2s ease;
        }
        .invoice-lane.lane-empty {
          background: transparent;
          border: 2px dashed #b0b8c4;
          min-height: 200px;
          opacity: 0.6;
        }
        .invoice-lane.lane-empty .lane-header {
          background: transparent;
        }
        .lane-empty-placeholder {
          text-align: center;
          padding: 1.5rem 0.75rem;
          color: var(--secondary);
          font-size: 0.75rem;
          opacity: 0.5;
        }

        /* Drop target feedback */
        .invoice-lane.lane-drag-over {
          background: rgba(59, 130, 246, 0.06);
          box-shadow: inset 0 0 0 2px var(--primary);
          border-radius: 12px;
        }
        .invoice-lane.lane-drop-invalid {
          opacity: 0.3;
          filter: grayscale(0.5);
        }

        .lane-header {
          padding: 0.875rem 1rem;
          background: white;
          border-radius: 12px 12px 0 0;
          border-bottom: 1px solid #f1f5f9;
        }
        .lane-date-filter {
          display: block;
          width: 100%;
          margin-top: 0.5rem;
          padding: 0.25rem 0.375rem;
          font-size: 0.6875rem;
          color: var(--secondary);
          background: #f8f9fb;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          outline: none;
        }
        .lane-date-filter:focus {
          border-color: var(--primary);
        }
        .lane-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 24px;
          height: 24px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0 0.5rem;
        }
        .lane-cards {
          padding: 0.5rem;
          overflow-y: auto;
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .lane-cards::-webkit-scrollbar { width: 4px; }
        .lane-cards::-webkit-scrollbar-track { background: transparent; }
        .lane-cards::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

        /* Invoice Card */
        .invoice-card {
          background: white;
          border: 1px solid #e8ecf1;
          border-radius: 8px;
          padding: 0.75rem 0.875rem;
          cursor: grab;
          transition: all 0.15s ease;
          user-select: none;
        }
        .invoice-card:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
        .invoice-card.is-dragging {
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
          border-color: var(--primary);
          opacity: 0.95;
        }
        .invoice-card-overdue {
          background: #fff5f5;
        }
        .invoice-card.drag-disabled {
          cursor: default;
        }
        .drag-handle {
          color: #d1d5db;
          opacity: 0;
          transition: opacity 0.15s ease;
          flex-shrink: 0;
        }
        .invoice-card:hover .drag-handle {
          opacity: 1;
        }
        .card-invoice-number {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
          flex: 1;
          font-weight: 600;
          font-size: 0.8125rem;
          color: var(--title);
        }
        .card-icon {
          width: 22px; height: 22px;
          border-radius: 5px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        /* Undo button */
        .undo-btn {
          opacity: 0;
          background: none;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          padding: 2px 4px;
          cursor: pointer;
          color: #d97706;
          transition: opacity 0.15s, background-color 0.15s, color 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          line-height: 1;
        }
        .invoice-card:hover .undo-btn {
          opacity: 1;
        }
        .undo-btn:hover {
          background-color: #fef3c7;
          border-color: #d97706;
          color: #92400e;
        }

        /* Search input */
        .board-search {
          position: relative;
        }
        .board-search input {
          padding-left: 2.25rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          height: 38px;
          width: 280px;
          transition: all 0.2s ease;
          background: white;
        }
        .board-search input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
          outline: none;
        }
        .board-search .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--secondary);
          opacity: 0.5;
        }

        @media (max-width: 1024px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
        }
      `}</style>

      <PageTitle activeMenu="Leverantörsfakturor" motherMenu="Inköp" />
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {invoices.length === 0 && !loading ? (
          <>
            <NoData message="Det finns inga leverantörsfakturor att visa. Skapa en för att kunna se dem här." />
            <div className="d-flex justify-content-center p-4">
              <Button variant="primary" onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}>
                <Plus size={16} className="me-2" />
                Skapa ny leverantörsfaktura
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Financial Dashboard — Stats Grid */}
            <div className="stats-grid mb-4">
              {/* Card 1: Väntar godkännande */}
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#fffbeb', color: '#f59e0b' }}>
                  <Clock size={24} />
                </div>
                <div className="stat-value" style={{ color: '#f59e0b' }}>
                  {formatCurrency(metrics?.total_pending_amount || computedMetrics.scanned_amount)}
                </div>
                <div className="stat-label">Väntar godkännande</div>
                <div className="stat-subtitle">
                  {metrics?.pending_payment_count || computedMetrics.scanned_count} leverantörsfakturor
                </div>
              </div>

              {/* Card 2: Förfallna */}
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
                  <AlertTriangle size={24} />
                </div>
                <div className="stat-value" style={{ color: '#dc2626' }}>
                  {formatCurrency(metrics?.total_overdue_amount || computedMetrics.overdue_amount)}
                </div>
                <div className="stat-label">Förfallna</div>
                <div className="stat-subtitle">
                  {metrics?.overdue_count || computedMetrics.overdue_count} förfallna fakturor
                </div>
              </div>

              {/* Card 3: Betalda */}
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#f0fdf4', color: '#059669' }}>
                  <CheckCircle size={24} />
                </div>
                <div className="stat-value" style={{ color: '#059669' }}>
                  {formatCurrency(metrics?.total_paid_amount || computedMetrics.paid_amount)}
                </div>
                <div className="stat-label">Betalda</div>
                <div className="stat-subtitle">
                  {metrics?.paid_count || computedMetrics.paid_count} betalda fakturor
                </div>
              </div>

              {/* Card 4: Effektivitet */}
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#f0f9ff', color: '#0284c7' }}>
                  <TrendingUp size={24} />
                </div>
                <div className="stat-value" style={{ color: '#0284c7' }}>
                  {metrics?.processing_efficiency || computedMetrics.efficiency}%
                </div>
                <div className="stat-label">Effektivitet</div>
                <div className="stat-subtitle">
                  {metrics?.processed_count || computedMetrics.processed_count} av {metrics?.total_invoices || computedMetrics.total_invoices} bearbetade
                </div>
              </div>
            </div>

            {/* Toolbar */}
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <div className="board-search">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Sök fakturanummer eller leverantör..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <select
                  className="form-select form-select-sm"
                  style={{ width: 'auto', fontSize: '0.875rem', border: '1px solid #e2e8f0', borderRadius: '8px', height: '38px' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Alla ({invoices.length})</option>
                  {LANE_CONFIG.map(lane => (
                    <option key={lane.key} value={lane.key}>
                      {lane.label} ({statusCounts[lane.key] || 0})
                    </option>
                  ))}
                </select>

                <Button
                  variant={showOverdueOnly ? 'danger' : 'outline-danger'}
                  size="sm"
                  onClick={() => setShowOverdueOnly(prev => !prev)}
                  className="d-flex align-items-center gap-1"
                  style={{ height: '38px', borderRadius: '8px' }}
                >
                  <AlertTriangle size={14} />
                  Förfallna
                  {overdueCount > 0 && (
                    <span
                      className="badge"
                      style={{
                        backgroundColor: showOverdueOnly ? 'white' : '#dc2626',
                        color: showOverdueOnly ? '#dc2626' : 'white',
                        fontSize: '0.625rem',
                        marginLeft: '4px',
                        minWidth: '18px',
                        lineHeight: '1'
                      }}
                    >
                      {overdueCount}
                    </span>
                  )}
                </Button>

                <Button
                  variant="light"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  style={{ height: '38px', width: '38px', padding: 0, borderRadius: '8px', border: '1px solid #e2e8f0' }}
                >
                  <RefreshCw size={16} className={refreshing ? 'spin' : ''} style={{ color: 'var(--secondary)' }} />
                </Button>
              </div>

              <Button
                variant="primary"
                onClick={() => history.push(`/${companyId}/create-supplier-invoices`)}
                className="d-flex align-items-center"
                style={{ borderRadius: '8px', height: '38px' }}
              >
                <Plus size={16} className="me-2" />
                Ny Leverantörsfaktura
              </Button>
            </div>

            {refreshing && (
              <div className="text-center mb-3" style={{ fontSize: '0.8125rem', color: 'var(--secondary)' }}>
                <Loader2 size={14} className="me-1 spin" />
                Uppdaterar...
              </div>
            )}

            {/* Kanban Board with Drag & Drop */}
            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="invoice-board">
                {LANE_CONFIG.map(lane => {
                  const laneInvoices = groups[lane.key] || [];
                  const isEmpty = laneInvoices.length === 0;
                  const isTerminal = lane.key === 'Paid';
                  const showUndoForLane = lane.key === 'Approved' || lane.key === 'Paid';
                  const isValidDrop = isValidDropTarget(lane.key);
                  const total = laneInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);

                  return (
                    <Droppable
                      droppableId={lane.key}
                      key={lane.key}
                      isDropDisabled={!isValidDrop}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={[
                            'invoice-lane',
                            isEmpty && !snapshot.isDraggingOver ? 'lane-empty' : '',
                            snapshot.isDraggingOver ? 'lane-drag-over' : '',
                            dragSourceStatus && !isValidDrop ? 'lane-drop-invalid' : ''
                          ].filter(Boolean).join(' ')}
                        >
                          {/* Lane header */}
                          <div className="lane-header">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                              <span style={{ fontWeight: '600', fontSize: '0.8125rem', color: isEmpty && !snapshot.isDraggingOver ? 'var(--secondary)' : 'var(--title)' }}>
                                {lane.label}
                              </span>
                              {(!isEmpty || snapshot.isDraggingOver) && (
                                <span className="lane-count" style={{ background: lane.bg, color: lane.color }}>
                                  {isTerminal && paidDays > 0 && statusCounts['Paid']
                                    ? `${laneInvoices.length} av ${statusCounts['Paid']}`
                                    : laneInvoices.length}
                                </span>
                              )}
                            </div>
                            {!isEmpty && (
                              <div style={{ fontSize: '0.75rem', fontWeight: '600', color: lane.color }}>
                                {formatCurrency(total)}
                              </div>
                            )}
                            {isTerminal && (
                              <select
                                className="lane-date-filter"
                                value={paidDays}
                                onChange={(e) => setPaidDays(Number(e.target.value))}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value={7}>Senaste 7 dagar</option>
                                <option value={30}>Senaste 30 dagar</option>
                                <option value={90}>Senaste 90 dagar</option>
                                <option value={0}>Visa alla</option>
                              </select>
                            )}
                          </div>

                          {/* Lane cards */}
                          <div className="lane-cards">
                            {isEmpty && !snapshot.isDraggingOver ? (
                              <div className="lane-empty-placeholder">
                                {dragSourceStatus && isValidDrop
                                  ? 'Släpp här'
                                  : 'Inga fakturor'}
                              </div>
                            ) : (
                              laneInvoices.map((invoice, index) => {
                                const isDragDisabled = invoice.status === 'Paid';
                                return (
                                  <Draggable
                                    key={invoice.invoice_id}
                                    draggableId={invoice.invoice_id}
                                    index={index}
                                    isDragDisabled={isDragDisabled}
                                  >
                                    {(provided, snapshot) => {
                                      const child = (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                        >
                                          <SupplierInvoiceCard
                                            invoice={invoice}
                                            onClick={() => !snapshot.isDragging && history.push(`/${companyId}/supplier-invoices/${invoice.invoice_id}`)}
                                            isDragging={snapshot.isDragging}
                                            showUndo={showUndoForLane}
                                            onUndo={handleUndo}
                                          />
                                        </div>
                                      );
                                      // Portal the dragging element to body to avoid
                                      // offset issues from CSS transforms on ancestors
                                      if (snapshot.isDragging) {
                                        return ReactDOM.createPortal(child, document.body);
                                      }
                                      return child;
                                    }}
                                  </Draggable>
                                );
                              })
                            )}
                            {provided.placeholder}
                          </div>
                        </div>
                      )}
                    </Droppable>
                  );
                })}
              </div>
            </DragDropContext>
          </>
        )}
      </div>

      {/* Verification Modal for accrual approval from kanban */}
      {/* VerificationModal for accrual approval */}
      {approvalInvoice && !paymentInvoice && (
        <VerificationModal
          show={showVerificationModal}
          onHide={() => { setShowVerificationModal(false); setApprovalInvoice(null); }}
          onConfirm={handleAccrualApprovalConfirm}
          invoice={approvalInvoice}
          invoiceId={approvalInvoice?.invoice_id}
          companyId={companyId}
          verificationType="accrual_invoice"
          isLoading={false}
          error={error}
          setError={setError}
        />
      )}

      {/* VerificationModal for cash payment */}
      {paymentInvoice && (
        <VerificationModal
          show={showVerificationModal}
          onHide={() => { setShowVerificationModal(false); setPaymentInvoice(null); setKanbanPaymentDate(null); setKanbanPaymentAmount(null); }}
          onConfirm={handleCashPaymentConfirm}
          invoice={paymentInvoice}
          invoiceId={paymentInvoice?.invoice_id}
          companyId={companyId}
          verificationType="cash_payment"
          overrideAmount={kanbanPaymentAmount}
          isLoading={false}
          error={error}
          setError={setError}
        />
      )}
    </>
  );
};

export default SupplierInvoiceList;
